import React, {useCallback, useEffect, useState} from "react";
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Typography} from "@mui/material";
import {Link as RouterLink} from "react-router-dom";
import {getBackendErrorMessage} from "../../../shared/utils/httpError";
import {getCobranca} from "../FinanceiroService";
import {getMeioDaCobrancaLabel} from "../cobrancasUtils";
import {useAcoesDaCobranca} from "../useAcoesDaCobranca";
import CobrancaCard from "./CobrancaCard";
import EventosDaCobranca from "./EventosDaCobranca";
import RegistrarPagamentoDialog from "./RegistrarPagamentoDialog";

function Dado({rotulo, children}) {
    return (
        <Box sx={{minWidth: 0}}>
            <Typography variant="caption" color="text.secondary">{rotulo}</Typography>
            <Box sx={{wordBreak: 'break-word'}}>{children}</Box>
        </Box>
    );
}

/*
 * O detalhe de uma cobrança (BBF-158): de quem é, que assinaturas ela cobre, o boleto com Pix e o histórico do banco.
 */
export default function CobrancaDetailsDialog({open, cobrancaId, nomeDaEmpresa, onClose, onAbrirFatura, aoMudar}) {
    const [cobranca, setCobranca] = useState(null);
    const [erro, setErro] = useState('');

    const carregar = useCallback(async () => {
        if (!cobrancaId) return;
        setErro('');
        try {
            const response = await getCobranca(cobrancaId);
            setCobranca(response?.data || null);
        } catch (error) {
            setErro(getBackendErrorMessage(error, 'Não foi possível carregar a cobrança.'));
        }
    }, [cobrancaId]);

    useEffect(() => {
        if (open) {
            setCobranca(null);
            carregar();
        }
    }, [open, carregar]);

    const depoisDoPagamento = useCallback(async () => {
        await carregar();
        await aoMudar?.();
    }, [carregar, aoMudar]);

    const acoes = useAcoesDaCobranca(depoisDoPagamento);

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {cobranca ? `${getMeioDaCobrancaLabel(cobranca.meio)} · ${nomeDaEmpresa?.(cobranca.empresaId) || 'Empresa'}` : 'Cobrança'}
            </DialogTitle>
            <DialogContent>
                {erro ? <Alert severity="error">{erro}</Alert> : null}
                {!erro && !cobranca ? <Typography color="text.secondary">Carregando a cobrança...</Typography> : null}
                {cobranca ? (
                    <Stack spacing={2.5}>
                        {acoes.aviso.success ? <Alert severity="success" onClose={acoes.limparAviso}>{acoes.aviso.success}</Alert> : null}
                        {acoes.aviso.error ? <Alert severity="error" onClose={acoes.limparAviso}>{acoes.aviso.error}</Alert> : null}

                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 2}}>
                            <Dado rotulo="Empresa">
                                <Link component={RouterLink} to="/empresas">{nomeDaEmpresa?.(cobranca.empresaId) || cobranca.empresaId}</Link>
                            </Dado>
                            <Dado rotulo="Fatura">
                                {onAbrirFatura ? (
                                    <Link component="button" type="button" onClick={() => onAbrirFatura(cobranca.faturaId)}>Abrir a fatura</Link>
                                ) : <Typography variant="body2">{cobranca.faturaId}</Typography>}
                            </Dado>
                            <Dado rotulo={cobranca.licencaIds?.length > 1 ? 'Assinaturas' : 'Assinatura'}>
                                {cobranca.licencaIds?.length ? (
                                    <Stack spacing={0.25}>
                                        {cobranca.licencaIds.map((licencaId) => (
                                            <Link key={licencaId} component={RouterLink} to={`/licencas/${licencaId}`} variant="body2">
                                                Ver a assinatura
                                            </Link>
                                        ))}
                                    </Stack>
                                ) : <Typography variant="body2">-</Typography>}
                            </Dado>
                            <Dado rotulo="Conector">
                                <Typography variant="body2">{cobranca.conector || '-'}</Typography>
                            </Dado>
                        </Box>

                        <CobrancaCard
                            cobranca={cobranca}
                            ocupado={acoes.ocupado}
                            onRegistrarPagamento={acoes.abrirPagamento}
                            onReenviarBoleto={acoes.reenviar}
                        />

                        <Box>
                            <Typography fontWeight={600} sx={{mb: 1}}>Retornos e eventos</Typography>
                            <EventosDaCobranca eventos={cobranca.eventos} />
                        </Box>
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
            <RegistrarPagamentoDialog
                open={Boolean(acoes.pagando)}
                cobranca={acoes.pagando}
                onClose={acoes.fecharPagamento}
                onConfirm={acoes.registrar}
            />
        </Dialog>
    );
}
